import { useEffect, useState } from "react";
import { Check, X, Trash2, ShieldCheck, FileText, Film } from "lucide-react";
import { toast } from "sonner";
import { adminListProfessionals, adminVerifyProfessional, adminDeleteProfessional, adminAadhaarBlob, adminAadhaarVerify } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;
const TABS = [["", "All"], ["pending", "Pending"], ["verified", "Verified"], ["rejected", "Rejected"]];

export default function AdminProfessionals() {
  const [tab, setTab] = useState("pending");
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null);
  const [note, setNote] = useState("");

  const load = () => adminListProfessionals(tab).then((d) => setList(d.professionals || [])).catch(() => {});
  useEffect(() => { load(); }, [tab]);

  const act = async (p, status) => {
    await adminVerifyProfessional(p.id, { verification_status: status, verification_note: note });
    toast.success(`Marked ${status}`);
    setSel(null); setNote(""); load();
  };
  const del = async (p) => { if (!window.confirm(`Delete ${p.name}?`)) return; await adminDeleteProfessional(p.id); setSel(null); load(); };

  const viewAadhaar = async (p, side) => {
    try {
      const blob = await adminAadhaarBlob(p.id, side);
      window.open(URL.createObjectURL(blob), "_blank");
    } catch { toast.error("Could not load Aadhaar"); }
  };

  const toggleAadhaar = async (p) => {
    try {
      await adminAadhaarVerify(p.id, !p.aadhaar_verified);
      toast.success(p.aadhaar_verified ? "Aadhaar verification removed" : "Aadhaar marked verified");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
  };

  return (
    <div className="mx-auto max-w-5xl p-6" data-testid="admin-professionals">
      <h1 className="mb-1 text-2xl font-semibold text-ink">Professionals — Verification</h1>
      <p className="mb-4 text-sm text-ink-3">Review astrologer / guru / purohit / SME applications. Aadhaar is private and only opens for admins.</p>
      <div className="mb-4 flex gap-2">
        {TABS.map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`protab-${v || "all"}`}
            className={`rounded-full border px-3 py-1 text-sm ${tab === v ? "border-saffron bg-[rgba(199,80,0,0.08)] text-saffron" : "border-line text-ink-2"}`}>{l}</button>
        ))}
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {list.map((p) => (
          <div key={p.id} data-testid={`pro-card-${p.id}`} className="border border-line bg-surface p-4">
            <div className="flex items-start gap-3">
              {p.photo_url ? <img src={`${API}${p.photo_url}`} alt={p.name} className="h-14 w-14 rounded-full object-cover" /> : <div className="h-14 w-14 rounded-full bg-surface-alt" />}
              <div className="min-w-0 flex-1">
                <p className="font-medium text-ink">{p.name} <span className="text-xs text-ink-3">· {p.type}</span></p>
                <p className="text-xs text-ink-3">{p.city} · {(p.languages || []).join(", ")}</p>
                <p className="mt-0.5 text-xs text-ink-3">{(p.specialities || []).join(" · ")}</p>
              </div>
              <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase ${p.verification_status === "verified" ? "bg-green-500/10 text-green-600" : p.verification_status === "rejected" ? "bg-red-500/10 text-red-500" : "bg-amber-500/10 text-amber-600"}`}>{p.verification_status}</span>
            </div>
            {p.bio && <p className="mt-2 line-clamp-2 text-sm text-ink-3">{p.bio}</p>}
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
              {p.intro_video_url && <a href={`${API}${p.intro_video_url}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 border border-line px-2 py-1 text-ink-2"><Film className="h-3 w-3" />Intro video</a>}
              {p.aadhaar_front_uploaded && <button onClick={() => viewAadhaar(p, "front")} className="inline-flex items-center gap-1 border border-line px-2 py-1 text-ink-2" data-testid={`aadhaar-front-${p.id}`}><FileText className="h-3 w-3" />Aadhaar front</button>}
              {p.aadhaar_back_uploaded && <button onClick={() => viewAadhaar(p, "back")} className="inline-flex items-center gap-1 border border-line px-2 py-1 text-ink-2" data-testid={`aadhaar-back-${p.id}`}><FileText className="h-3 w-3" />Aadhaar back</button>}
              {p.aadhaar_verified && <span className="inline-flex items-center gap-1 rounded-full bg-green-500/10 px-2 py-1 font-medium text-green-700" data-testid={`aadhaar-verified-${p.id}`}><ShieldCheck className="h-3 w-3" />Aadhaar verified</span>}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <button onClick={() => { setSel(p.id === sel ? null : p.id); setNote(p.verification_note || ""); }} className="text-xs text-saffron">{sel === p.id ? "Close" : "Review"}</button>
              <button onClick={() => del(p)} data-testid={`pro-del-${p.id}`} className="ml-auto text-ink-3 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
            </div>
            {sel === p.id && (
              <div className="mt-3 border-t border-line pt-3">
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Verification note (optional)" className="mb-2 w-full border border-line bg-parchment px-2 py-1.5 text-sm text-ink" data-testid={`pro-note-${p.id}`} />
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => act(p, "verified")} data-testid={`pro-verify-${p.id}`} className="inline-flex items-center gap-1 bg-green-600 px-3 py-1.5 text-xs font-medium text-white"><ShieldCheck className="h-3.5 w-3.5" />Verify & publish</button>
                  <button onClick={() => act(p, "rejected")} data-testid={`pro-reject-${p.id}`} className="inline-flex items-center gap-1 border border-red-300 px-3 py-1.5 text-xs text-red-500"><X className="h-3.5 w-3.5" />Reject</button>
                  <button onClick={() => toggleAadhaar(p)} disabled={!(p.aadhaar_front_uploaded && p.aadhaar_back_uploaded)} data-testid={`pro-aadhaar-verify-${p.id}`}
                    title={!(p.aadhaar_front_uploaded && p.aadhaar_back_uploaded) ? "Both Aadhaar front and back must be uploaded first" : ""}
                    className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-40 ${p.aadhaar_verified ? "border border-line text-ink-2" : "bg-saffron text-white"}`}>
                    <ShieldCheck className="h-3.5 w-3.5" />{p.aadhaar_verified ? "Unverify Aadhaar" : "Mark Aadhaar verified"}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
        {list.length === 0 && <p className="text-sm text-ink-3">No applications in this view.</p>}
      </div>
    </div>
  );
}
